import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FaArrowLeft, FaSave } from "react-icons/fa";
import "./BreakdownTracker1.css";
import "./AddUser.css";

const STATIC_TRUCKS = [
  '2895DK','3734WB','867BA','5865BK','5439AK','247DK','8178DK','2578RH','5427AK','9305ML',
  '8269WB','1258MR','4451MS','2627RH','4947DK','5743HK','242BK','2674YM','154HK',
  '3757AK','1589WB','9011BA','7178HK','4412WA','4090DK','3658MS','6927YS','8563YS','8259DA','6386ML','7606WB',
  '5380BK','6641MS','1553YS','9537YW','2271MS','693HA','6655RA','8111MS','9192MB','4702MA','9637AK','8411YR',
  '371YM','9906MS','3381HW','4896BM','8784RA','113DS','3467DA','6820RA','6905MH','5524RH','1323MS','2691RA',
  '897BM','3270WA','4611ML','4425RA','8089RK','193RH','3883DA','2817ML','7684MH','7135RA','4427MR','7025MS',
  '980MH','4726MA','7745MR','7058MH','3218MS','1780YS','520BM','961MH','4093HK','277MS','9073HW','7378YR',
  '1804BA','6807MB','3710WK','4985YR','7103HA','4520HK','629YR','3255DK','6502MA','7330WK','8938YW','1771RA',
  '8524WK','1030DA','2557YM','5622BK','624RA','3847MK','4556MK','6678MA','4220ML','3526MB','5142MB','1550MK',
  '3167MD','1477MD','144RA','9299YW','50YM','899HM','2227WK','8180BM', '9742RA'
];

const AddDriver = () => {
  const [formData, setFormData] = useState({
    reporting_driver_code: "",
    driver_name: "",
    is_active: "Y",
    is_expat: "N",
    inactive_date: "",
    remarks: "",
    truck_number: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showTruckDropdown, setShowTruckDropdown] = useState(false);
  const [truckSearchTerm, setTruckSearchTerm] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  // normalize: keys to lowercase, treat "NULL"/"null" as null
  const normalizeVal = (v) => {
    if (v === undefined || v === null) return null;
    if (typeof v === "string") {
      const t = v.trim();
      if (t === "" || t.toLowerCase() === "null") return null;
      return t;
    }
    return v;
  };

  const fetchDriverDetails = useCallback(async (driverId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/drivers/${driverId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const raw = response.data || {};
      const d = {};
      Object.keys(raw).forEach((k) => {
        d[k.toLowerCase()] = raw[k];
      });

      const formattedInactiveDate = d.inactive_date
        ? new Date(d.inactive_date).toISOString().split("T")[0]
        : "";

      const normalizedTruck = normalizeVal(d.truck_number) || "";

      setFormData({
        reporting_driver_code: normalizeVal(d.reporting_driver_code) || "",
        driver_name: normalizeVal(d.driver_name) || "",
        is_active: normalizeVal(d.is_active) || "Y",
        is_expat: normalizeVal(d.is_expat) || "N",
        inactive_date: formattedInactiveDate,
        remarks: normalizeVal(d.remarks) || "",
        truck_number: normalizedTruck,
      });

      setTruckSearchTerm(normalizedTruck);
    } catch (error) {
      console.error("Error fetching driver details:", error);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchDriverDetails(id);
      setIsEditing(true);
    }
  }, [id, fetchDriverDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTruckSelect = (truck) => {
    setFormData((prev) => ({ ...prev, truck_number: truck }));
    setTruckSearchTerm(truck);
    setShowTruckDropdown(false);
  };

  const handleTruckFocus = () => {
    setShowTruckDropdown(true);
    if (!truckSearchTerm) {
      setTruckSearchTerm("");
    }
  };

  const filteredTrucks = STATIC_TRUCKS.filter((truck) =>
    truck.toLowerCase().includes(truckSearchTerm.toLowerCase())
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("token");
      const submitData = {
        ...formData,
        inactive_date: formData.inactive_date || null,
        remarks: formData.remarks || null,
        truck_number: formData.truck_number || null,
      };

      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_BASE_API_URL}/drivers/${id}`,
          submitData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert("Driver updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_BASE_API_URL}/drivers`,
          submitData,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        alert("Driver added successfully!");
      }
      navigate("/driver-management");
    } catch (error) {
      console.error("Error submitting driver:", error);
      alert(error.response?.data?.message || "Failed to submit driver. Please try again.");
    }
  };

  return (
    <div className="user-form-page">
      <div className="user-form-card">
        {/* Header with Arrow Back Button */}
        <div className="user-form-header">
          <div className="user-form-header-left">
            <button
              type="button"
              className="bt-back-btn"
              onClick={() => navigate("/driver-management")}
              title="Back"
            >
              <FaArrowLeft />
            </button>
            <div>
              <h2 className="user-form-title">
                {isEditing ? "Edit Driver Details" : "Add New Driver"}
              </h2>
              <p className="user-form-subtitle">
                {isEditing
                  ? `Update driver information for ${formData.driver_name || formData.reporting_driver_code || ""}`
                  : "Register an approved driver into the JMCC fleet directory"}
              </p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="user-form-grid">
            {/* Driver Code */}
            <div className="user-form-field">
              <label className="user-form-label">
                Driver Code <span className="required-star">*</span>
              </label>
              <input
                type="text"
                className="user-form-input"
                name="reporting_driver_code"
                placeholder="e.g. DRV-1024"
                value={formData.reporting_driver_code}
                onChange={handleChange}
                required
              />
            </div>

            {/* Driver Name */}
            <div className="user-form-field">
              <label className="user-form-label">
                Driver Name <span className="required-star">*</span>
              </label>
              <input
                type="text"
                className="user-form-input"
                name="driver_name"
                placeholder="e.g. Ahmed Al-Balushi"
                value={formData.driver_name}
                onChange={handleChange}
                required
              />
            </div>

            {/* Status */}
            <div className="user-form-field">
              <label className="user-form-label">
                Status <span className="required-star">*</span>
              </label>
              <select
                className="user-form-select"
                name="is_active"
                value={formData.is_active}
                onChange={handleChange}
                required
              >
                <option value="Y">Active</option>
                <option value="N">Inactive</option>
              </select>
            </div>

            {/* Expat Driver */}
            <div className="user-form-field">
              <label className="user-form-label">
                Driver Type <span className="required-star">*</span>
              </label>
              <select
                className="user-form-select"
                name="is_expat"
                value={formData.is_expat}
                onChange={handleChange}
                required
              >
                <option value="N">Local Driver</option>
                <option value="Y">Expat Driver</option>
              </select>
            </div>

            {/* Inactive Date */}
            <div className="user-form-field">
              <label className="user-form-label">Inactive Date</label>
              <input
                type="date"
                className="user-form-input"
                name="inactive_date"
                value={formData.inactive_date}
                onChange={handleChange}
              />
            </div>

            {/* Truck Number Searchable */}
            <div className="user-form-field">
              <label className="user-form-label">Assigned Truck Number</label>
              <div style={{ position: "relative" }}>
                <input
                  type="text"
                  className="user-form-input"
                  placeholder="Search or select truck..."
                  value={truckSearchTerm}
                  onChange={(e) => setTruckSearchTerm(e.target.value)}
                  onFocus={handleTruckFocus}
                  onBlur={() =>
                    setTimeout(() => setShowTruckDropdown(false), 200)
                  }
                />
                {showTruckDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      backgroundColor: "#fff",
                      border: "1px solid #cbd5e1",
                      borderTop: "none",
                      borderRadius: "0 0 10px 10px",
                      maxHeight: "180px",
                      overflowY: "auto",
                      zIndex: 20,
                      boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
                    }}
                  >
                    {filteredTrucks.length > 0 ? (
                      filteredTrucks.map((truck) => (
                        <div
                          key={truck}
                          onClick={() => handleTruckSelect(truck)}
                          style={{
                            padding: "9px 14px",
                            cursor: "pointer",
                            backgroundColor:
                              formData.truck_number === truck
                                ? "#eff6ff"
                                : "#fff",
                            borderBottom: "1px solid #f1f5f9",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#1e293b",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.backgroundColor = "#f8fafc")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.backgroundColor =
                              formData.truck_number === truck
                                ? "#eff6ff"
                                : "#fff")
                          }
                        >
                          {truck}
                        </div>
                      ))
                    ) : (
                      <div
                        style={{
                          padding: "12px",
                          color: "#94a3b8",
                          textAlign: "center",
                          fontSize: "13px",
                        }}
                      >
                        No matching trucks found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Remarks */}
            <div className="user-form-field full-width">
              <label className="user-form-label">Remarks</label>
              <textarea
                className="user-form-input"
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="3"
                placeholder="Optional notes or remarks..."
                style={{ height: "auto", minHeight: "80px", padding: "10px" }}
              />
            </div>

            {/* Action buttons */}
            <div className="user-form-actions">
              <button
                type="button"
                className="user-cancel-btn"
                onClick={() => navigate("/driver-management")}
              >
                Cancel
              </button>
              <button type="submit" className="user-submit-btn">
                <FaSave /> {isEditing ? "Update Driver" : "Save Driver"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDriver;
